/**
 * Analyse concurrentielle.
 *
 * L'écran répond à une question : quoi construire ? Les reproches sont des
 * manques à combler, les éloges des forces à égaler. Chaque ligne porte sa
 * citation et un lien vers la discussion d'origine — un insight non vérifiable
 * ne sert à rien.
 */
import { useState } from 'react';
import type { CompetitorInsight, CompetitorMentionDetail, MentionAspect } from '@vigie/shared';
import { api } from '../api.js';
import { Card, Empty, Field, Notice } from '../components.js';
import { formatDay, useAction, useAsync } from '../hooks.js';

const ASPECT_LABEL: Record<MentionAspect, string> = {
  pricing: 'tarif',
  reliability: 'fiabilité',
  features: 'fonctionnalités',
  support: 'support',
  ux: 'ergonomie',
  integration: 'intégration',
  other: 'autre',
};

/** Barre comparative reproches / éloges pour un aspect donné. */
function AspectBar({ complaints, praises }: { complaints: number; praises: number }) {
  const total = Math.max(1, complaints + praises);
  return (
    <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', minWidth: 90 }}>
      <div
        style={{ width: `${(complaints / total) * 100}%`, background: 'var(--danger)' }}
        title={`${complaints} reproche(s)`}
      />
      <div
        style={{ width: `${(praises / total) * 100}%`, background: 'var(--ok)' }}
        title={`${praises} éloge(s)`}
      />
    </div>
  );
}

function MentionList({
  mentions,
  kind,
}: {
  mentions: CompetitorMentionDetail[];
  kind: 'complaint' | 'praise';
}) {
  if (mentions.length === 0) {
    return <Empty>{kind === 'complaint' ? 'Aucun reproche relevé.' : 'Aucun éloge relevé.'}</Empty>;
  }
  return (
    <div className="stack">
      {mentions.map((mention) => (
        <div key={mention.id}>
          <div className="inline">
            <span className={kind === 'complaint' ? 'badge danger' : 'badge ok'}>
              {ASPECT_LABEL[mention.aspect]}
            </span>
            <span className="small muted">{formatDay(mention.hitPostedAt)}</span>
          </div>
          <div style={{ marginTop: 4 }}>{mention.summary}</div>
          {mention.quote ? (
            <blockquote
              style={{
                margin: '6px 0 0',
                paddingLeft: 10,
                borderLeft: '2px solid var(--border)',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              « {mention.quote} »
            </blockquote>
          ) : null}
          <a className="small mono" href={mention.hitUrl} target="_blank" rel="noreferrer">
            {mention.hitTitle.slice(0, 70)} ↗
          </a>
        </div>
      ))}
    </div>
  );
}

function InsightCard({ insight, onChanged }: { insight: CompetitorInsight; onChanged: () => void }) {
  const { run, busy } = useAction();
  const [open, setOpen] = useState(insight.mentions > 0);

  return (
    <Card
      title={
        <div className="inline">
          <button type="button" className="ghost" onClick={() => setOpen(!open)}>
            {open ? '▾' : '▸'}
          </button>
          <h2>{insight.name}</h2>
          <span className="badge">{insight.mentions} mention(s)</span>
          {insight.complaints > 0 ? (
            <span className="badge danger">{insight.complaints} reproche(s)</span>
          ) : null}
          {insight.praises > 0 ? (
            <span className="badge ok">{insight.praises} éloge(s)</span>
          ) : null}
          {insight.comparisons > 0 ? (
            <span className="badge accent">{insight.comparisons} comparaison(s)</span>
          ) : null}
        </div>
      }
      actions={
        <button
          type="button"
          className="small danger"
          disabled={busy}
          onClick={async () => {
            if (!confirm(`Ne plus suivre ${insight.name} ?`)) return;
            await run(() => api.deleteCompetitor(insight.competitorId));
            onChanged();
          }}
        >
          Retirer
        </button>
      }
    >
      {insight.mentions === 0 ? (
        <Empty>
          Aucune mention dans le corpus. Lancez l’analyse, ou vérifiez les variantes d’écriture.
        </Empty>
      ) : open ? (
        <div className="stack">
          <table>
            <thead>
              <tr>
                <th>Aspect</th>
                <th className="num">Reproches</th>
                <th className="num">Éloges</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {insight.byAspect.map((aspect) => (
                <tr key={aspect.aspect}>
                  <td>{ASPECT_LABEL[aspect.aspect]}</td>
                  <td className="num">{aspect.complaints}</td>
                  <td className="num">{aspect.praises}</td>
                  <td>
                    <AspectBar complaints={aspect.complaints} praises={aspect.praises} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="detail-grid">
            <div>
              <h3 style={{ marginBottom: 8 }}>Ce qu’on leur reproche</h3>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Autant de manques que vous pouvez combler.
              </div>
              <MentionList mentions={insight.topComplaints} kind="complaint" />
            </div>
            <div>
              <h3 style={{ marginBottom: 8 }}>Ce qu’on leur reconnaît</h3>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Ce qui fait leur succès : à égaler avant de vouloir faire mieux.
              </div>
              <MentionList mentions={insight.topPraises} kind="praise" />
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export function Competitors() {
  const sites = useAsync(() => api.sites(), []);
  const [siteId, setSiteId] = useState('');
  const activeSite = siteId || sites.data?.[0]?.id || '';

  const insights = useAsync(
    () => (activeSite ? api.competitorInsights(activeSite) : Promise.resolve([])),
    [activeSite],
  );
  const { run, busy, error } = useAction();

  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [lastRun, setLastRun] = useState<string | null>(null);

  const addCompetitor = async () => {
    const created = await run(() =>
      api.createCompetitor(activeSite, {
        name,
        aliases: aliases
          .split(',')
          .map((alias) => alias.trim())
          .filter((alias) => alias !== ''),
      }),
    );
    if (created) {
      setName('');
      setAliases('');
      insights.reload();
    }
  };

  const analyze = async () => {
    const result = await run(() => api.analyzeCompetitors(activeSite, { limit: 300 }));
    if (result) {
      setLastRun(
        result.error
          ? `Analyse interrompue : ${result.error}`
          : `${result.hitsScanned} discussion(s) relues, ${result.hitsMatched} citant un concurrent, ` +
            `${result.mentionsCreated} nouvelle(s) mention(s), ${result.skipped} déjà analysée(s).`,
      );
      insights.reload();
    }
  };

  return (
    <>
      <header className="topbar">
        <h1>Concurrence</h1>
        <span className="spacer" />
        <div className="inline">
          <select
            value={activeSite}
            onChange={(event) => setSiteId(event.target.value)}
            style={{ width: 180 }}
          >
            {(sites.data ?? []).map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
          <button type="button" className="primary small" onClick={analyze} disabled={busy || !activeSite}>
            {busy ? 'Analyse en cours…' : 'Analyser le corpus'}
          </button>
        </div>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {insights.error ? <Notice kind="error">{insights.error}</Notice> : null}
        {lastRun ? <Notice kind="success">{lastRun}</Notice> : null}

        <Notice>
          L’analyse relit les discussions <strong>déjà collectées</strong> — elle n’interroge aucune
          plateforme. Comme pour la collecte, un préfiltre gratuit écarte les discussions qui ne
          citent aucun concurrent : le modèle n’est appelé que sur les autres.
        </Notice>

        <Card title="Suivre un concurrent">
          <div className="row">
            <div>
              <label>Nom</label>
              <input
                value={name}
                placeholder="UptimeRobot"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <Field
              label="Variantes d’écriture (séparées par des virgules)"
              help="Les gens écrivent « uptime robot », « UptimeRobot », parfois un diminutif. Chaque variante manquée est une mention perdue."
            >
              <input
                value={aliases}
                placeholder="uptime robot, urobot"
                onChange={(event) => setAliases(event.target.value)}
              />
            </Field>
            <div className="shrink">
              <button type="button" onClick={addCompetitor} disabled={busy || !name || !activeSite}>
                Ajouter
              </button>
            </div>
          </div>
        </Card>

        {(insights.data ?? []).map((insight) => (
          <InsightCard key={insight.competitorId} insight={insight} onChanged={insights.reload} />
        ))}

        {insights.data && insights.data.length === 0 ? (
          <Card>
            <Empty>
              Aucun concurrent suivi. Ajoutez-en un ci-dessus, puis lancez l’analyse sur le corpus
              déjà collecté.
            </Empty>
          </Card>
        ) : null}
      </div>
    </>
  );
}
